import { dtoConverter, HttpUtils, INSPECTIONS_STORE, updateBatch, WAREHOUSES_STORE, WW_INSPECTIONS_COLLECTION } from '@damofire/data-commons';
import { QueryConstraint } from '@firebase/firestore';
import { expose } from 'comlink';
import { collection, doc, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, query, Query, setDoc, startAfter, where } from 'firebase/firestore';
import { OPERATION } from '../../des.dto';
import { Inspection } from '../../inspection/inspection.dto';
import { Warehouse } from '../../warehouse/warehouse.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { logger } from '@sentry/angular';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseInspectionsStepsWorker extends AbstractWarehouseDataSyncStepWorker<Inspection> {
  constructor() {
    super(INSPECTIONS_STORE, 'INSPECTIONS');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.info('Inspections clean step starting', { category: 'sync', division: ctx.division });
    return this.deleteAllDataObsoleteFromStore<Inspection>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async push(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: Inspection; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    logger.info('Inspections push step starting', { category: 'sync', division: ctx.division });
    return super.push(ctx, onProgress, itemsProcessedCallback);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: Inspection[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    try {
      const q: Query = query(
        collection(getFirestore(), WW_INSPECTIONS_COLLECTION),
        where('division._id', '==', ctx.division),
        orderBy('audit.lastModifiedDate'),
        ...queryConstraints,
      ).withConverter(dtoConverter({ syncId: ctx.syncId }));
      const queryResult = await getDocs(q);

      if (queryResult.docs.length && !this.shouldStopTask) {
        const inspections = queryResult.docs.map((d) => d.data());

        try {
          logger.info('Inspections downloaded from Firestore', { category: 'sync', count: inspections.length, division: ctx.division });
          await this.idbService.updateAll(this.store, inspections).catch(console.error);
          return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
        } catch (err) {
          logger.error('Cannot update inspections in indexedDB', { category: 'sync', division: ctx.division, error: err });
          throw err;
        }
      }
    } catch (err) {
      logger.error('Cannot download inspections from Firestore', { category: 'sync', division: ctx.division, error: err });
      throw err;
    }

    return [null, 0];
  }

  protected async updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    const warehouseData: Warehouse = await this.idbService.getByKey(WAREHOUSES_STORE, warehouse);
    if (warehouseData?.statistics?.inspections?.total) {
      const newProgress = Math.ceil((downloaded / warehouseData.statistics?.inspections?.total) * 100);
      logger.debug('Inspections download progress', { category: 'sync', progress: newProgress, warehouse });
      onProgress(newProgress, { processed: downloaded, expected: warehouseData.statistics.inspections.total });
    }
  }

  protected async addRemoteData(data: Inspection[]): Promise<void> {
    for (const inspection of data) {
      const { issues, ...inspectionNoStats } = inspection;
      const inspectionDocRef = doc(getFirestore(), `${WW_INSPECTIONS_COLLECTION}/${inspection._id}`);

      await setDoc(inspectionDocRef, inspectionNoStats);
    }
  }

  protected async deleteRemoteData(data: Inspection[]): Promise<void> {
    const byWarehouse = data.reduce(
      (byWarehouse: Record<string, Inspection[]>, inspection: Inspection) => {
        if (!byWarehouse[inspection.division._id]) {
          byWarehouse[inspection.division._id] = [];
        }

        byWarehouse[inspection.division._id].push(inspection);
        return byWarehouse;
      },
      {} as Record<string, Inspection[]>,
    );

    for (const warehouse of Object.keys(byWarehouse)) {
      let deleteIssues = false;
      const ids = byWarehouse[warehouse].map((i: Inspection) => {
        if (i.deleteIssues) {
          deleteIssues = true;
        }
        return i._id;
      });

      try {
        await HttpUtils.post(this.environment.appConfig.fns, 'ww-inspectionsDeleteAll', {
          data: {
            inspectionIds: ids,
            divisionId: warehouse,
            deleteIssues,
          },
        });
      } catch (err) {
        logger.error('Cannot delete inspections on remote', { category: 'sync', warehouse, error: err });
      }
    }
  }

  protected async updateRemoteData(data: Inspection[]): Promise<void> {
    data = data.map((inspection) => {
      const { issues, ...inspectionNoStats } = inspection;
      return inspectionNoStats;
    });
    await updateBatch(WW_INSPECTIONS_COLLECTION, data);
  }
}

expose(WarehouseInspectionsStepsWorker);
